import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import { logger } from '@/utils/logger';
import integrationAPI from '@/apis/integration';
import { useIdentityStore } from '@/stores/identity';

export const useCommunityIntegrationsStore = defineStore('communityIntegrations', () => {
  const identityStore = useIdentityStore();

  const salesforceCaseFields = ref(null);
  const salesforceMapping = ref(null);
  const salesforceAccounts = ref([]);
  const selectedBrandIdForSalesforce = ref();
  const pending = ref({
    salesforceCaseFields: false,
    salesforceMapping: false,
  });

  const selectedBrandId = computed(
    () => selectedBrandIdForSalesforce.value ?? identityStore.currentBrand?.id,
  );
  const salesforceFieldOptions = computed(() => {
    if (!salesforceCaseFields.value) return [];

    const { required = [] } = salesforceCaseFields.value;

    const dynamicFields = required
      .filter((field) => field.type !== 'picklist')
      .map((field) => ({
        label: field.label,
        value: field.name,
        group: 'dynamic',
      }));

    const picklistFields = required
      .filter((field) => field.type === 'picklist')
      .map((field) => ({
        label: field.label,
        value: field.name,
        group: 'picklist',
      }));

    return [...dynamicFields, ...picklistFields];
  });

  const salesforceOptionalFieldOptions = computed(() => {
    if (!salesforceCaseFields.value) return [];

    const { optional = [] } = salesforceCaseFields.value;

    const dynamicFields = optional
      .filter((field) => field.type !== 'picklist')
      .map((field) => ({
        label: field.label,
        value: field.name,
        group: 'dynamic',
      }));

    const picklistFields = optional
      .filter((field) => field.type === 'picklist')
      .map((field) => ({
        label: field.label,
        value: field.name,
        group: 'picklist',
      }));

    return [...dynamicFields, ...picklistFields];
  });

  const salesforcePicklistValues = computed(() => {
    if (!salesforceCaseFields.value) return {};

    const { required = [], optional = [] } = salesforceCaseFields.value;
    const allFields = [...required, ...optional];

    const picklistFields = allFields.filter((field) => field.type === 'picklist');

    return picklistFields.reduce((acc, field) => {
      acc[field.name] = field.picklistValue.map((value) => ({
        label: value,
        value: value.toUpperCase(),
      }));
      return acc;
    }, {});
  });

  const recordTypeOptions = computed(() => {
    if (!salesforceCaseFields.value?.recordTypes) return [];

    return salesforceCaseFields.value.recordTypes
      .filter((rt) => rt.active && rt.available)
      .map((rt) => {
        if (rt.master) {
          return {
            ...rt,
            label: 'Use Default',
            value: rt.recordTypeId,
          };
        }
        return {
          ...rt,
          label: rt.name,
          value: rt.recordTypeId,
        };
      })
      .sort((a) => (a.master ? -1 : 1));
  });

  async function getSalesforceCaseFields(brandId) {
    pending.value.salesforceCaseFields = true;

    try {
      const response = await integrationAPI.getSalesforceCaseFields(brandId);
      salesforceCaseFields.value = response.data;
    } catch (error) {
      logger.error('Failed to fetch Salesforce case fields:', {}, error);
      throw error;
    } finally {
      pending.value.salesforceCaseFields = false;
    }
  }

  function clearSalesforceCaseFields() {
    salesforceCaseFields.value = null;
  }

  function normalizePicklistValues(mappings) {
    if (!mappings) return mappings;

    const normalizeFields = (fields) => {
      if (!fields) return fields;

      return Object.entries(fields).reduce((acc, [key, field]) => {
        if (field.type === 'picklist' && field.salesforcePicklist) {
          const normalizedPicklist = Object.entries(field.salesforcePicklist).reduce(
            (picklistAcc, [sfValue, dashValues]) => {
              picklistAcc[sfValue] = dashValues;
              return picklistAcc;
            },
            {},
          );

          acc[key] = {
            ...field,
            salesforcePicklist: normalizedPicklist,
          };
        } else {
          acc[key] = field;
        }
        return acc;
      }, {});
    };

    return {
      ...mappings,
      requiredFields: normalizeFields(mappings.requiredFields),
      optionalFields: normalizeFields(mappings.optionalFields),
    };
  }

  async function getSalesforceMapping(brandId) {
    pending.value.salesforceMapping = true;
    try {
      const response = await integrationAPI.getSalesforceMappings(brandId);
      if (response.data?.data?.mapping) {
        salesforceMapping.value = {
          brandId: response.data.data.brandId,
          mappings: normalizePicklistValues(response.data.data.mapping),
          status: response.data.data.status,
        };
      } else {
        salesforceMapping.value = null;
      }
    } catch (error) {
      logger.error('Failed to fetch Salesforce mapping', {}, error);
      salesforceMapping.value = null;
    } finally {
      pending.value.salesforceMapping = false;
    }
  }

  function salesforceMappingHasErrors() {
    if (!salesforceMapping.value) return false;
    return salesforceMapping.value.status !== 'valid';
  }

  async function createOrReplaceSalesforceMappings(data) {
    pending.value.salesforceMapping = true;
    try {
      const response = await integrationAPI.createOrReplaceSalesforceMappings(data);
      if (response.data?.data?.mapping) {
        salesforceMapping.value = {
          brandId: response.data.data.brandId,
          mappings: normalizePicklistValues(response.data.data.mapping),
        };
      }
      return response;
    } finally {
      pending.value.salesforceMapping = false;
    }
  }

  async function updateSalesforceMapping(data) {
    pending.value.salesforceMapping = true;
    try {
      const response = await integrationAPI.updateSalesforceMappings(data);
      if (response.data?.data?.mapping) {
        salesforceMapping.value = {
          brandId: response.data.data.brandId,
          mappings: normalizePicklistValues(response.data.data.mapping),
        };
      }
      return response;
    } finally {
      pending.value.salesforceMapping = false;
    }
  }

  async function getSalesforceAccount(brandIds) {
    try {
      const brandIdsArray = Array.isArray(brandIds) ? brandIds.join(',') : brandIds;
      const response = await integrationAPI.getSalesforceAccount(brandIdsArray);
      salesforceAccounts.value = response.data.data;
    } catch (error) {
      logger.error('Failed to fetch Salesforce account', {}, error);
      salesforceAccounts.value = [];
      throw error;
    }
  }

  return {
    salesforceCaseFields,
    salesforceMapping,
    pending,
    salesforceFieldOptions,
    salesforceOptionalFieldOptions,
    salesforcePicklistValues,
    recordTypeOptions,
    salesforceAccounts,
    selectedBrandId,
    selectedBrandIdForSalesforce,
    getSalesforceCaseFields,
    getSalesforceMapping,
    createOrReplaceSalesforceMappings,
    updateSalesforceMapping,
    clearSalesforceCaseFields,
    salesforceMappingHasErrors,
    getSalesforceAccount,
  };
});
